import axios from "../utils/AxiosInstance";
import { ResponseMessage } from "../models/ResponseMessage";
import { Constants } from "../constants/constants";
import {
  CreateTestimonialRequest,
  CreateWebsiteMediaRequest,
  CreateWebsiteStatRequest,
  EnumMediaType,
  mediaTypeToEnum,
  Testimonial,
  UpdateTestimonialRequest,
  UpdateWebsiteMediaRequest,
  UpdateWebsiteSettingRequest,
  UpdateWebsiteStatRequest,
  WebsiteMedia,
  WebsiteSettings,
  WebsiteStat,
} from "../models/WebsiteModels";

const API_BASE_URL = Constants.API_BASE_URL;

// ===================== Settings =====================

export async function getWebsiteSettingsByOrganizationAsync(
  token: string,
  organizationId: string,
): Promise<ResponseMessage<WebsiteSettings>> {
  const response = await axios.get(
    `${API_BASE_URL}/websitesettings/organization/${organizationId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function updateWebsiteSettingsAsync(
  token: string,
  id: string,
  settings: UpdateWebsiteSettingRequest,
): Promise<ResponseMessage<WebsiteSettings>> {
  const data = new FormData();
  if (settings.themeNumber != null)
    data.append("themeNumber", String(settings.themeNumber));
  if (settings.brandColor) data.append("brandColor", settings.brandColor);
  if (settings.appointmentImageUrl)
    data.append("appointmentImageUrl", settings.appointmentImageUrl);
  if (settings.taglineImageUrl)
    data.append("taglineImageUrl", settings.taglineImageUrl);
  if (settings.isActive != null)
    data.append("isActive", String(settings.isActive));
  if (settings.appointmentImageFile)
    data.append("appointmentImageFile", settings.appointmentImageFile);
  if (settings.taglineImageFile)
    data.append("taglineImageFile", settings.taglineImageFile);

  const response = await axios.put(
    `${API_BASE_URL}/websitesettings/${id}`,
    data,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

// ===================== Media (promo + before/after) =====================

export async function getWebsiteMediaByOrganizationAsync(
  organizationId: string,
): Promise<ResponseMessage<WebsiteMedia[]>> {
  const response = await axios.get(
    `${API_BASE_URL}/websitemedia/organization/${organizationId}`,
  );

  return ResponseMessage.fromJson(response.data);
}

export async function createWebsiteMediaAsync(
  token: string,
  media: CreateWebsiteMediaRequest,
): Promise<ResponseMessage<WebsiteMedia>> {
  const data = new FormData();
  data.append("organizationId", media.organizationId);
  data.append("mediaType", String(mediaTypeToEnum(media.mediaType)));
  if (media.title) data.append("title", media.title);
  if (media.description) data.append("description", media.description);
  data.append("sortOrder", String(media.sortOrder ?? 0));
  data.append("isActive", String(media.isActive ?? true));
  if (media.imageFile) data.append("imageFile", media.imageFile);
  if (media.beforeImageFile) data.append("beforeImageFile", media.beforeImageFile);
  if (media.afterImageFile) data.append("afterImageFile", media.afterImageFile);

  const response = await axios.post(`${API_BASE_URL}/websitemedia`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function updateWebsiteMediaAsync(
  token: string,
  id: string,
  media: UpdateWebsiteMediaRequest,
): Promise<ResponseMessage<WebsiteMedia>> {
  const data = new FormData();
  if (media.title) data.append("title", media.title);
  if (media.description) data.append("description", media.description);
  if (media.imageUrl) data.append("imageUrl", media.imageUrl);
  if (media.beforeImageUrl) data.append("beforeImageUrl", media.beforeImageUrl);
  if (media.afterImageUrl) data.append("afterImageUrl", media.afterImageUrl);
  if (media.sortOrder !== undefined)
    data.append("sortOrder", String(media.sortOrder));
  if (media.isActive !== undefined)
    data.append("isActive", String(media.isActive));
  if (media.imageFile) data.append("imageFile", media.imageFile);
  if (media.beforeImageFile) data.append("beforeImageFile", media.beforeImageFile);
  if (media.afterImageFile) data.append("afterImageFile", media.afterImageFile);

  const response = await axios.put(`${API_BASE_URL}/websitemedia/${id}`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function deleteWebsiteMediaAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<boolean>> {
  const response = await axios.delete(`${API_BASE_URL}/websitemedia/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

// ===================== Stats =====================

export async function getWebsiteStatsByOrganizationAsync(
  organizationId: string,
): Promise<ResponseMessage<WebsiteStat[]>> {
  const response = await axios.get(
    `${API_BASE_URL}/websitestats/organization/${organizationId}`,
  );

  return ResponseMessage.fromJson(response.data);
}

export async function createWebsiteStatAsync(
  token: string,
  stat: CreateWebsiteStatRequest,
): Promise<ResponseMessage<WebsiteStat>> {
  const data = new FormData();
  data.append("organizationId", stat.organizationId);
  if (stat.icon) data.append("icon", stat.icon);
  if (stat.iconFile) data.append("iconFile", stat.iconFile);
  data.append("value", stat.value);
  data.append("label", stat.label);
  data.append("sortOrder", String(stat.sortOrder ?? 0));
  data.append("isActive", String(stat.isActive ?? true));

  const response = await axios.post(`${API_BASE_URL}/websitestats`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function updateWebsiteStatAsync(
  token: string,
  id: string,
  stat: UpdateWebsiteStatRequest,
): Promise<ResponseMessage<WebsiteStat>> {
  const data = new FormData();
  if (stat.icon) data.append("icon", stat.icon);
  if (stat.value) data.append("value", stat.value);
  if (stat.label) data.append("label", stat.label);
  if (stat.sortOrder !== undefined)
    data.append("sortOrder", String(stat.sortOrder));
  if (stat.isActive !== undefined)
    data.append("isActive", String(stat.isActive));
  if (stat.iconFile) data.append("iconFile", stat.iconFile);

  const response = await axios.put(`${API_BASE_URL}/websitestats/${id}`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function deleteWebsiteStatAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<boolean>> {
  const response = await axios.delete(`${API_BASE_URL}/websitestats/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

// ===================== Testimonials =====================

export async function getTestimonialsByOrganizationAsync(
  organizationId: string,
): Promise<ResponseMessage<Testimonial[]>> {
  const response = await axios.get(
    `${API_BASE_URL}/testimonials/organization/${organizationId}`,
  );

  return ResponseMessage.fromJson(response.data);
}

export async function createTestimonialAsync(
  token: string,
  testimonial: CreateTestimonialRequest,
): Promise<ResponseMessage<Testimonial>> {
  const response = await axios.post(
    `${API_BASE_URL}/testimonials`,
    testimonial,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function updateTestimonialAsync(
  token: string,
  id: string,
  testimonial: UpdateTestimonialRequest,
): Promise<ResponseMessage<Testimonial>> {
  const response = await axios.put(
    `${API_BASE_URL}/testimonials/${id}`,
    testimonial,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return ResponseMessage.fromJson(response.data);
}

export async function deleteTestimonialAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<boolean>> {
  const response = await axios.delete(`${API_BASE_URL}/testimonials/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}